import { createArtificialIdAlfanumerico } from '../../common/utils';
import {
  PlanillaPartidoJugador,
  PlanillaPartidoJugadorJSON,
} from '../PlanillaPartidoJugador/PlanillaPartidoJugador';
import {
  PlanillaPartidoOficial,
  PlanillaPartidoOficialJSON,
} from '../PlanillaPartidoOficial/PlanillaPartidoOficial';
import {
  PlanillaPartidoOficialAuxiliar,
  PlanillaPartidoOficialAuxiliarJSON,
} from '../PlanillaPartidoOficialAuxiliar/PlanillaPartidoOficialAuxiliar';
import { TipoEventoPlanilla, TipoEventoPlanillaJSON } from '../TipoEventoPlanilla/TipoEventoPlanilla';

export interface EventoXPlanillaJSON {
  exp_id: string | null;
  exp_ppid: number;
  exp_teveid: number;
  exp_minuto: string;
  exp_ppjid: number | null;
  exp_ppoid: number | null;
  exp_ppoaid: number | null;
  exp_activo: boolean;
  tipoEventoPlanilla?: TipoEventoPlanillaJSON;
  planillaPartidoJugador?: PlanillaPartidoJugadorJSON;
  planillaPartidoOficial?: PlanillaPartidoOficialJSON;
  planillaPartidoOficialAuxiliar?: PlanillaPartidoOficialAuxiliarJSON;
}

export class EventoXPlanilla {
  private idArtificialRef;
  protected constructor(
    private id: string | null,
    private ppid: number,
    private teveid: number,
    private minuto: string,
    private ppjid: number | null,
    private ppoid: number | null,
    private ppoaid: number | null,
    private duracionEnSegundos: number | null,
    private hasBeenSaved: boolean,
    private hasPendingCallback: boolean,
    private activo: boolean,
    private tipoEventoPlanilla?: TipoEventoPlanilla,
    private planillaPartidoJugador?: PlanillaPartidoJugador,
    private planillaPartidoOficial?: PlanillaPartidoOficial,
    private planillaPartidoOficialAuxiliar?: PlanillaPartidoOficialAuxiliar
  ) {
    this.idArtificialRef = createArtificialIdAlfanumerico();
  }

  static createFrom(
    id: string | null,
    ppid: number,
    teveid: number,
    minuto: string,
    ppjid: number | null,
    ppoid: number | null,
    ppoaid: number | null,
    duracionEnSegundos: number | null,
    activo: boolean,
    tipoEventoPlanilla?: TipoEventoPlanilla,
    planillaPartidoJugador?: PlanillaPartidoJugador,
    planillaPartidoOficial?: PlanillaPartidoOficial,
    planillaPartidoOficialAuxiliar?: PlanillaPartidoOficialAuxiliar
  ) {
    return new this(
      id,
      ppid,
      teveid,
      minuto,
      ppjid,
      ppoid,
      ppoaid,
      duracionEnSegundos,
      false,
      false,
      activo,
      tipoEventoPlanilla,
      planillaPartidoJugador,
      planillaPartidoOficial,
      planillaPartidoOficialAuxiliar
    );
  }

  static createForJugador(
    planillaPartidoId: number,
    tipoEvento: TipoEventoPlanilla,
    minutoActual: string,
    ppjId: number | null,
    tiempoEnSegundos: number | null,
    ppj?: PlanillaPartidoJugador
  ) {
    return EventoXPlanilla.createFrom(
      'new',
      +planillaPartidoId!,
      +tipoEvento.getId()!,
      minutoActual,
      ppjId,
      null,
      null,
      tiempoEnSegundos,
      true,
      tipoEvento,
      ppj,
      undefined,
      undefined
    );
  }

  static createForOficial(
    planillaPartidoId: number,
    tipoEvento: TipoEventoPlanilla,
    minutoActual: string,
    ppoId: number | null,
    tiempoEnSegundos: number | null,
    ppo: PlanillaPartidoOficial
  ) {
    return EventoXPlanilla.createFrom(
      'new',
      +planillaPartidoId!,
      +tipoEvento.getId()!,
      minutoActual,
      null,
      ppoId,
      null,
      tiempoEnSegundos,
      true,
      tipoEvento,
      undefined,
      ppo,
      undefined
    );
  }

  static createForOficialAuxiliar(
    planillaPartidoId: number,
    tipoEvento: TipoEventoPlanilla,
    minutoActual: string,
    ppoaId: number | null,
    tiempoEnSegundos: number | null,
    ppoa: PlanillaPartidoOficialAuxiliar
  ) {
    return EventoXPlanilla.createFrom(
      'new',
      +planillaPartidoId!,
      +tipoEvento.getId()!,
      minutoActual,
      null,
      null,
      ppoaId,
      tiempoEnSegundos,
      true,
      tipoEvento,
      undefined,
      undefined,
      ppoa
    );
  }

  isIdentifiedBy(anId: string): boolean {
    return this.id === anId;
  }

  isIdentifiedAs(aEntity: EventoXPlanilla) {
    if (this.id) {
      return aEntity.isIdentifiedBy(this.id);
    } else {
      return false;
    }
  }

  static fromJSON(entity: EventoXPlanillaJSON) {
    return EventoXPlanilla.createFrom(
      entity.exp_id,
      entity.exp_ppid,
      entity.exp_teveid,
      entity.exp_minuto,
      entity.exp_ppjid,
      entity.exp_ppoid,
      entity.exp_ppoaid,
      0,
      entity.exp_activo,
      entity.tipoEventoPlanilla ? TipoEventoPlanilla.fromJSON(entity.tipoEventoPlanilla) : undefined,
      entity.planillaPartidoJugador
        ? PlanillaPartidoJugador.fromJSON(entity.planillaPartidoJugador)
        : undefined,
      entity.planillaPartidoOficial
        ? PlanillaPartidoOficial.fromJSON(entity.planillaPartidoOficial)
        : undefined,
      entity.planillaPartidoOficialAuxiliar
        ? PlanillaPartidoOficialAuxiliar.fromJSON(entity.planillaPartidoOficialAuxiliar)
        : undefined
    );
  }

  toJSON(): EventoXPlanillaJSON {
    return {
      exp_id: this.getId(),
      exp_ppid: this.getPpid(),
      exp_teveid: this.getTeveid(),
      exp_minuto: this.getMinuto(),
      exp_ppjid: this.getPpjid(),
      exp_ppoid: this.getPpoid(),
      exp_ppoaid: this.getPpoaid(),
      exp_activo: this.getActivo(),
      tipoEventoPlanilla: this.tipoEventoPlanilla ? this.tipoEventoPlanilla.toJSON() : undefined,
      planillaPartidoJugador: this.planillaPartidoJugador ? this.planillaPartidoJugador.toJSON() : undefined,
      planillaPartidoOficial: this.planillaPartidoOficial ? this.planillaPartidoOficial.toJSON() : undefined,
      planillaPartidoOficialAuxiliar: this.planillaPartidoOficialAuxiliar
        ? this.planillaPartidoOficialAuxiliar.toJSON()
        : undefined,
    };
  }

  getActivo() {
    return this.activo;
  }

  getId(): string | null {
    return this.id;
  }

  getIdArtifical(): string {
    return this.idArtificialRef;
  }

  getPpid(): number {
    return this.ppid;
  }

  getTeveid(): number {
    return this.teveid;
  }

  getMinuto(): string {
    return this.minuto;
  }

  getPpjid() {
    return this.ppjid;
  }

  getPpoid() {
    return this.ppoid;
  }

  getPpoaid() {
    return this.ppoaid;
  }

  getDuracionEnSegundos() {
    return this.duracionEnSegundos;
  }
  getTipoEventoPlanilla() {
    return this.tipoEventoPlanilla;
  }
  getPlanillaPartidoJugador() {
    return this.planillaPartidoJugador;
  }
  getPlanillaPartidoOficial() {
    return this.planillaPartidoOficial;
  }
  getPlanillaPartidoOficialAuxiliar() {
    return this.planillaPartidoOficialAuxiliar;
  }

  isSaved() {
    return this.hasBeenSaved;
  }

  hasPendingCallbackInCourse() {
    return this.hasPendingCallback;
  }

  setHasPendingCallback(v: boolean) {
    this.hasPendingCallback = v;
  }

  setDuracionEnSegundos(v: number) {
    this.duracionEnSegundos = v;
  }

  getMinutoEnSegundos(): number {
    return +this.minuto.split(':')[0] * 60 + +this.minuto.split(':')[1];
  }

  saved(event: EventoXPlanilla) {
    this.id = event.getId();
    this.hasBeenSaved = true;
  }

  getTiempoRestante(_tiempoActual: string) {
    const tiempoEvento = this.getMinuto().split(':');
    const tiempoActual = _tiempoActual.split(':');
    const segundoActual = +tiempoActual[0] * 60 + +tiempoActual[1];
    const segundoInicioEvento = +tiempoEvento[0] * 60 + +tiempoEvento[1];
    const segundoFinEvento = segundoInicioEvento + this.duracionEnSegundos!;

    let diferenciaSegundos = segundoFinEvento - segundoActual;

    return diferenciaSegundos <= 0 ? 0 : diferenciaSegundos;
  }

  undo() {
    this.activo = false;
    this.hasBeenSaved = false;
  }
}
